import * as THREE from "three";
import { warn } from "../../lib/log";

// --- Pick-Volume geometry helpers (Hotfix 5.0 §3e) -------------------------------------------
// Isolate the connected component (welded triangles) that contains the clicked face, so tagging
// grabs ONLY the clicked element (wall / pod / slab) and not merged neighbours in the same mesh.
export function componentWorldVerts(mesh, face) {
  const geom = mesh.geometry;
  const pos = geom?.attributes?.position;
  if (!pos || !face) return null;
  const index = geom.index;
  const triCount = index ? index.count / 3 : pos.count / 3;
  const vidx = (t, k) => (index ? index.getX(t * 3 + k) : t * 3 + k);
  const keyOf = (i) => `${Math.round(pos.getX(i) * 1000)},${Math.round(pos.getY(i) * 1000)},${Math.round(pos.getZ(i) * 1000)}`;
  // Weld coincident vertices.
  const weldId = new Map();
  const vWeld = new Int32Array(pos.count);
  for (let i = 0; i < pos.count; i++) {
    const k = keyOf(i);
    let id = weldId.get(k);
    if (id === undefined) { id = weldId.size; weldId.set(k, id); }
    vWeld[i] = id;
  }
  // triangles touching each welded vertex.
  const trisOf = new Map();
  for (let t = 0; t < triCount; t++) {
    for (let k = 0; k < 3; k++) {
      const w = vWeld[vidx(t, k)];
      let arr = trisOf.get(w); if (!arr) { arr = []; trisOf.set(w, arr); }
      arr.push(t);
    }
  }
  // BFS from the clicked triangle's welded vertices across shared vertices.
  const seedWelds = [vWeld[face.a], vWeld[face.b], vWeld[face.c]];
  const seenTri = new Uint8Array(triCount);
  const seenWeld = new Set();
  const stack = [];
  seedWelds.forEach((w) => { if (!seenWeld.has(w)) { seenWeld.add(w); stack.push(w); } });
  const compVertKeys = new Set();
  let guard = 0;
  while (stack.length && guard++ < 2_000_00) {
    const w = stack.pop();
    const tris = trisOf.get(w) || [];
    for (const t of tris) {
      if (seenTri[t]) continue;
      seenTri[t] = 1;
      for (let k = 0; k < 3; k++) {
        const vi = vidx(t, k);
        compVertKeys.add(vi);
        const nw = vWeld[vi];
        if (!seenWeld.has(nw)) { seenWeld.add(nw); stack.push(nw); }
      }
    }
  }
  if (compVertKeys.size < 3) return null;
  mesh.updateWorldMatrix(true, false);
  const mw = mesh.matrixWorld;
  const out = [];
  const tmp = new THREE.Vector3();
  compVertKeys.forEach((i) => { out.push(tmp.fromBufferAttribute(pos, i).clone().applyMatrix4(mw)); });
  return out;
}

// Like componentWorldVerts, but returns the FULL connected component as actual world-space triangle
// positions (so an irregular / folded / sloped solid is preserved instead of a convex-hull prism),
// plus its true signed volume, Y extent and a point cloud for footprint hulling.
export function componentWorldTris(mesh, face) {
  const geom = mesh.geometry;
  const pos = geom?.attributes?.position;
  if (!pos || !face) return null;
  const index = geom.index;
  const triCount = index ? index.count / 3 : pos.count / 3;
  const vidx = (t, k) => (index ? index.getX(t * 3 + k) : t * 3 + k);
  const keyOf = (i) => `${Math.round(pos.getX(i) * 1000)},${Math.round(pos.getY(i) * 1000)},${Math.round(pos.getZ(i) * 1000)}`;
  const weldId = new Map();
  const vWeld = new Int32Array(pos.count);
  for (let i = 0; i < pos.count; i++) {
    const k = keyOf(i);
    let id = weldId.get(k);
    if (id === undefined) { id = weldId.size; weldId.set(k, id); }
    vWeld[i] = id;
  }
  const trisOf = new Map();
  for (let t = 0; t < triCount; t++) {
    for (let k = 0; k < 3; k++) {
      const w = vWeld[vidx(t, k)];
      let arr = trisOf.get(w); if (!arr) { arr = []; trisOf.set(w, arr); }
      arr.push(t);
    }
  }
  const seedWelds = [vWeld[face.a], vWeld[face.b], vWeld[face.c]];
  const seenTri = new Uint8Array(triCount);
  const seenWeld = new Set();
  const stack = [];
  seedWelds.forEach((w) => { if (!seenWeld.has(w)) { seenWeld.add(w); stack.push(w); } });
  const compTris = [];
  let guard = 0;
  while (stack.length && guard++ < 2_000_00) {
    const w = stack.pop();
    const tris = trisOf.get(w) || [];
    for (const t of tris) {
      if (seenTri[t]) continue;
      seenTri[t] = 1;
      compTris.push(t);
      for (let k = 0; k < 3; k++) {
        const nw = vWeld[vidx(t, k)];
        if (!seenWeld.has(nw)) { seenWeld.add(nw); stack.push(nw); }
      }
    }
  }
  if (!compTris.length) return null;
  mesh.updateWorldMatrix(true, false);
  const mw = mesh.matrixWorld;
  const positions = new Float32Array(compTris.length * 9);
  const verts = [];
  const a = new THREE.Vector3(), b = new THREE.Vector3(), c = new THREE.Vector3(), cr = new THREE.Vector3();
  let p = 0, yMin = Infinity, yMax = -Infinity, vol6 = 0;
  compTris.forEach((t) => {
    a.fromBufferAttribute(pos, vidx(t, 0)).applyMatrix4(mw);
    b.fromBufferAttribute(pos, vidx(t, 1)).applyMatrix4(mw);
    c.fromBufferAttribute(pos, vidx(t, 2)).applyMatrix4(mw);
    [a, b, c].forEach((vv) => {
      positions[p++] = vv.x; positions[p++] = vv.y; positions[p++] = vv.z;
      if (vv.y < yMin) yMin = vv.y; if (vv.y > yMax) yMax = vv.y;
      verts.push(vv.clone());
    });
    vol6 += a.dot(cr.crossVectors(b, c)); // 6× signed tetra volume about origin
  });
  return { positions, verts, yMin, yMax, volume: Math.abs(vol6) / 6 };
}


// Dominant horizontal axes of a point cloud via 2×2 PCA on the XZ (ground) projection, so the
// tagged box hugs the geometry's true orientation instead of the world axes.
export function principalAxesXZ(verts) {
  let mx = 0, mz = 0;
  for (const v of verts) { mx += v.x; mz += v.z; }
  mx /= verts.length; mz /= verts.length;
  let cxx = 0, cxz = 0, czz = 0;
  for (const v of verts) { const dx = v.x - mx, dz = v.z - mz; cxx += dx * dx; cxz += dx * dz; czz += dz * dz; }
  const theta = 0.5 * Math.atan2(2 * cxz, cxx - czz);
  const u = new THREE.Vector3(Math.cos(theta), 0, Math.sin(theta));
  const w = new THREE.Vector3(-Math.sin(theta), 0, Math.cos(theta));
  return { u, w };
}

// Convex hull of a point cloud's XZ (ground) projection — Andrew's monotone chain. Returns the
// hull as Vector2 (x,z) in CCW order. Used to extrude a picked volume's EXACT footprint outline
// (tightly hugging the geometry) instead of a looser oriented bounding box.
export function convexHullXZ(verts) {
  let pts = verts.map((p) => [+p.x.toFixed(4), +p.z.toFixed(4)]);
  pts.sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  pts = pts.filter((p, i) => i === 0 || p[0] !== pts[i - 1][0] || p[1] !== pts[i - 1][1]);
  if (pts.length < 3) return pts.map(([x, z]) => new THREE.Vector2(x, z));
  const cross = (o, a, b) => (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);
  const lower = [];
  for (const p of pts) { while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop(); lower.push(p); }
  const upper = [];
  for (let i = pts.length - 1; i >= 0; i--) { const p = pts[i]; while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop(); upper.push(p); }
  lower.pop(); upper.pop();
  return lower.concat(upper).map(([x, z]) => new THREE.Vector2(x, z));
}


// World-space Y extent of the building-layer meshes (excludes context) — used to
// distinguish Ground-Level from Rooftop for up-facing faces (positional raycasting).
export function buildingBoundsY(meshes) {
  const box = new THREE.Box3();
  let has = false;
  (meshes || []).forEach((m) => {
    if (m && m.userData?.layerRole !== "context") { box.expandByObject(m); has = true; }
  });
  return has ? { minY: box.min.y, maxY: box.max.y } : { minY: 0, maxY: 1 };
}

// Semantic tag from a face's world normal + world hit point + model bounds.
// Up-facing (n.y>0.9) near the building's minY => Ground-Level; above => Rooftop/Podium.
// Sloped up-faces => Roof/Terrace. Vertical faces (n.y<0.1) => Facade by cardinal.
export function semanticTag(worldNormal, worldPoint, bounds) {
  const n = worldNormal;
  if (n.y > 0.9) {
    const minY = bounds?.minY ?? 0, maxY = bounds?.maxY ?? 1;
    const tol = Math.max((maxY - minY) * 0.04, 0.5);
    if (worldPoint && Math.abs(worldPoint.y - minY) <= tol) {
      return { tag: "Ground-Level", locationId: "ground", kind: "ground" };
    }
    return { tag: "Rooftop / Podium", locationId: "rooftop", kind: "roof" };
  }
  if (n.y >= 0.1) return { tag: "Roof / Terrace", locationId: "rooftop", kind: "roof" };
  let locationId;
  if (Math.abs(n.x) >= Math.abs(n.z)) locationId = n.x >= 0 ? "facade_e" : "facade_w";
  else locationId = n.z >= 0 ? "facade_s" : "facade_n";
  const label = { facade_e: "Facade – East", facade_w: "Facade – West", facade_s: "Facade – South", facade_n: "Facade – North" }[locationId];
  return { tag: label, locationId, kind: "facade" };
}

// Passive face identification — names the hovered surface by true-compass orientation (matching the
// north-aware bucketing used in computeGross) or roof/ground, WITHOUT selecting the face. Returns a
// short human label + kind for the cursor popup.
export function orientationLabel(n, worldPoint, bounds, northDeg = 0) {
  if (n.y > 0.9) {
    const minY = bounds?.minY ?? 0, maxY = bounds?.maxY ?? 1;
    const tol = Math.max((maxY - minY) * 0.04, 0.5);
    if (worldPoint && Math.abs(worldPoint.y - minY) <= tol) return { tag: "Ground", kind: "ground" };
    return { tag: "Roof", kind: "roof" };
  }
  if (n.y >= 0.1) return { tag: "Roof / Terrace", kind: "roof" };
  if (n.y < -0.5) return { tag: "Underside / Soffit", kind: "under" };
  let heading = (Math.atan2(n.x, -n.z) * 180) / Math.PI + (northDeg || 0);
  heading = ((heading % 360) + 360) % 360;
  const bucket = heading < 45 || heading >= 315 ? "N" : heading < 135 ? "E" : heading < 225 ? "S" : "W";
  const label = { N: "North Façade", E: "East Façade", S: "South Façade", W: "West Façade" }[bucket];
  return { tag: label, kind: "facade", bucket };
}

// Which gross-area bucket a surface auto-falls into — drives the classification engine override.
export function bucketFromNormal(n, worldPoint, bounds, northDeg = 0) {
  const oi = orientationLabel(n, worldPoint, bounds, northDeg);
  if (oi.kind === "roof") return "roof";
  if (oi.kind === "ground") return "base";
  if (oi.kind === "facade") return oi.bucket;
  return null;
}

// Collect the coplanar triangle cluster around a hit face (the full architectural face),
// in world coords, offset slightly along the normal. Returns { geometry, area } or null.
export function buildCoplanarHighlight(mesh, worldNormal, worldPoint, modelScale = 20) {
  const geo = mesh.geometry;
  const pos = geo?.attributes?.position;
  if (!pos) return null;
  const index = geo.index;
  const mw = mesh.matrixWorld;
  const triCount = index ? index.count / 3 : pos.count / 3;
  const d = worldNormal.dot(worldPoint);
  const tol = Math.max(modelScale * 0.006, 0.005); // coplanarity tolerance, relative to model size
  const vA = new THREE.Vector3(), vB = new THREE.Vector3(), vC = new THREE.Vector3();
  const ab = new THREE.Vector3(), ac = new THREE.Vector3(), nn = new THREE.Vector3();
  const getV = (i, tgt) => { const vi = index ? index.getX(i) : i; return tgt.fromBufferAttribute(pos, vi).applyMatrix4(mw); };
  const out = [];
  let area = 0;
  for (let t = 0; t < triCount; t++) {
    getV(t * 3, vA); getV(t * 3 + 1, vB); getV(t * 3 + 2, vC);
    ab.subVectors(vB, vA); ac.subVectors(vC, vA);
    nn.crossVectors(ab, ac);
    const len = nn.length();
    if (len < 1e-9) continue;
    nn.multiplyScalar(1 / len);
    if (nn.dot(worldNormal) < 0.985) continue;                 // same facing
    if (Math.abs(worldNormal.dot(vA) - d) > tol) continue;      // same plane
    out.push(vA.x, vA.y, vA.z, vB.x, vB.y, vB.z, vC.x, vC.y, vC.z);
    area += len * 0.5;
  }
  if (!out.length) return null;
  const eps = Math.max(modelScale * 0.0015, 0.002); // paint just above the surface (scale-relative, no float)
  for (let i = 0; i < out.length; i += 3) { out[i] += worldNormal.x * eps; out[i + 1] += worldNormal.y * eps; out[i + 2] += worldNormal.z * eps; }
  const g = new THREE.BufferGeometry();
  g.setAttribute("position", new THREE.BufferAttribute(new Float32Array(out), 3));
  return { geometry: g, area };
}

// A rectangle THREE.Shape spanning (0,0)→(W,H) — used for bbox-fallback face overlays.
export function rectShape(W, H) {
  const s = new THREE.Shape();
  s.moveTo(0, 0); s.lineTo(W, 0); s.lineTo(W, H); s.lineTo(0, H); s.closePath();
  return s;
}

// Shoelace area / centroid for a 2D polygon (Vector2[]).
export function polygonArea(pts) {
  let a = 0;
  for (let i = 0, n = pts.length; i < n; i++) { const p = pts[i], q = pts[(i + 1) % n]; a += p.x * q.y - q.x * p.y; }
  return Math.abs(a) / 2;
}
export function polygonCentroid(pts) {
  let cx = 0, cy = 0, a = 0;
  for (let i = 0, n = pts.length; i < n; i++) { const p = pts[i], q = pts[(i + 1) % n]; const cr = p.x * q.y - q.x * p.y; a += cr; cx += (p.x + q.x) * cr; cy += (p.y + q.y) * cr; }
  a *= 0.5;
  if (Math.abs(a) < 1e-9) { let mx = 0, my = 0; pts.forEach((p) => { mx += p.x; my += p.y; }); return new THREE.Vector2(mx / pts.length, my / pts.length); }
  return new THREE.Vector2(cx / (6 * a), cy / (6 * a));
}

// Extract the outer boundary loop of a coplanar triangle soup, in (u,v) coords relative to `origin`.
// Merges float-coincident vertices (quantized), keeps edges used exactly once (boundary), then
// chains them into loops and returns the largest-area loop. Returns { outline, area, centroid } or null.
export function extractOutline(arr, u, v, origin, quant) {
  const q = Math.max(quant, 1e-4);
  const verts = new Map(); // quant key -> vertex index
  const vpts = [];         // Vector2 (a,b) rel origin
  const P = new THREE.Vector3();
  const idxOf = (x, y, z) => {
    P.set(x, y, z).sub(origin);
    const a = P.dot(u), b = P.dot(v);
    const k = `${Math.round(a / q)},${Math.round(b / q)}`;
    let i = verts.get(k);
    if (i == null) { i = vpts.length; verts.set(k, i); vpts.push(new THREE.Vector2(a, b)); }
    return i;
  };
  const ek = (i, j) => (i < j ? `${i}_${j}` : `${j}_${i}`);
  const edgeCount = new Map();
  const tris = arr.length / 9;
  for (let t = 0; t < tris; t++) {
    const o = t * 9;
    const i0 = idxOf(arr[o], arr[o + 1], arr[o + 2]);
    const i1 = idxOf(arr[o + 3], arr[o + 4], arr[o + 5]);
    const i2 = idxOf(arr[o + 6], arr[o + 7], arr[o + 8]);
    [[i0, i1], [i1, i2], [i2, i0]].forEach(([i, j]) => { if (i === j) return; const k = ek(i, j); edgeCount.set(k, (edgeCount.get(k) || 0) + 1); });
  }
  const adj = new Map();
  const push = (i, j) => { if (!adj.has(i)) adj.set(i, []); adj.get(i).push(j); };
  edgeCount.forEach((c, k) => { if (c !== 1) return; const [i, j] = k.split("_").map(Number); push(i, j); push(j, i); });
  if (!adj.size) return null;
  const used = new Set();
  const loops = [];
  for (const seed of adj.keys()) {
    const first = (adj.get(seed) || []).find((nb) => !used.has(ek(seed, nb)));
    if (first == null) continue;
    const loop = [seed];
    let prev = seed, cur = first;
    used.add(ek(seed, first));
    let guard = 0;
    while (cur !== seed && guard++ < vpts.length + 5) {
      loop.push(cur);
      const nbrs = adj.get(cur) || [];
      let nxt = nbrs.find((x) => x !== prev && !used.has(ek(cur, x)));
      if (nxt == null) nxt = nbrs.find((x) => !used.has(ek(cur, x)));
      if (nxt == null) break;
      used.add(ek(cur, nxt));
      prev = cur; cur = nxt;
    }
    if (loop.length >= 3) loops.push(loop.map((i) => vpts[i].clone()));
  }
  if (!loops.length) return null;
  let best = loops[0], bestA = polygonArea(loops[0]);
  for (let i = 1; i < loops.length; i++) { const a = polygonArea(loops[i]); if (a > bestA) { bestA = a; best = loops[i]; } }
  if (best.length < 3 || bestA < 1e-6) return null;
  return { outline: best, area: bestA, centroid: polygonCentroid(best) };
}

// Full coplanar-face descriptor: exact highlight geometry + in-plane basis (u,v,n),
// bounding extents (W,H), min-corner origin, and the exact boundary outline (n-gon).
export function coplanarFace(mesh, worldNormal, worldPoint, modelScale) {
  const built = buildCoplanarHighlight(mesh, worldNormal, worldPoint, modelScale);
  if (!built) return null;
  const n = worldNormal.clone();
  const up = Math.abs(n.y) < 0.9 ? new THREE.Vector3(0, 1, 0) : new THREE.Vector3(1, 0, 0);
  const u = new THREE.Vector3().crossVectors(up, n).normalize();
  const v = new THREE.Vector3().crossVectors(n, u).normalize();
  const arr = built.geometry.attributes.position.array;
  let minA = Infinity, maxA = -Infinity, minB = Infinity, maxB = -Infinity;
  const P = new THREE.Vector3();
  for (let i = 0; i < arr.length; i += 3) { P.set(arr[i], arr[i + 1], arr[i + 2]).sub(worldPoint); const a = P.dot(u), b = P.dot(v); if (a < minA) minA = a; if (a > maxA) maxA = a; if (b < minB) minB = b; if (b > maxB) maxB = b; }
  const W = Math.max(maxA - minA, 0.01), H = Math.max(maxB - minB, 0.01);
  const origin = worldPoint.clone().addScaledVector(u, minA).addScaledVector(v, minB);
  const od = extractOutline(arr, u, v, origin, Math.max(modelScale * 0.0008, 0.003));
  let outline = od?.outline || null;
  let centroid = od?.centroid || null;
  // Guard: if the boundary loop's area strays far from the true surface area, the topology
  // was ambiguous (non-manifold / T-junctions) — drop it so callers use the bbox fallback.
  if (outline) {
    const oa = polygonArea(outline);
    if (!(oa > built.area * 0.5 && oa < built.area * 1.8)) { outline = null; centroid = null; }
  }
  // Pristine coplanar polygon — no CSG/2D-clipping. Native depth testing (depthTest:true on the
  // overlay) hides the parts sliding under upper masses, giving the "leftover space" illusion.
  const holes = null;
  const W2 = W, H2 = H;
  return { built, n, u, v, origin, W: W2, H: H2, area: built.area, outline, centroid, holes };
}

// Matte grey clay per Section 2 spec (color 0xd7d8d8, roughness 0.8)
export function clayMaterial() {
  return new THREE.MeshStandardMaterial({ color: 0xd7d8d8, roughness: 0.8, metalness: 0.0, envMapIntensity: 0.7 });
}

// Faint diagonal hatch texture for the "unused" (available-but-unutilised) face area (§11)
let _hatchTex = null;
export function hatchTexture() {
  if (_hatchTex) return _hatchTex;
  const c = document.createElement("canvas"); c.width = c.height = 16;
  const x = c.getContext("2d");
  x.strokeStyle = "rgba(255,255,255,0.9)"; x.lineWidth = 1.5;
  x.beginPath(); x.moveTo(-4, 12); x.lineTo(12, -4); x.moveTo(4, 20); x.lineTo(20, 4); x.stroke();
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  _hatchTex = t; return t;
}

// Crisp dark architectural outlines around a mesh (EdgesGeometry -> LineSegments)
export function addEdges(mesh) {
  try {
    const edges = new THREE.EdgesGeometry(mesh.geometry, 30);
    const line = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: 0x1f2429, transparent: true, opacity: 0.72 }));
    line.userData.isEdge = true;
    line.raycast = () => {}; // never block surface-area picking
    mesh.add(line);
  } catch (err) {
    warn("addEdges: geometry lacks a valid position attribute", err);
  }
}

export function rhinoLayerColor(layer) {
  const c = layer?.color;
  if (!c || typeof c !== "object") return "rgb(120,120,124)";
  return `rgb(${c.r ?? 120}, ${c.g ?? 120}, ${c.b ?? 124})`;
}

// North orientation is taken from the .3dm import (Rhino earth-anchor model-north) when
// present; otherwise the model's own imported orientation is used (0° offset).
export function deriveNorthDeg(root) {
  const s = root?.userData?.settings;
  const mn = s?.earthAnchorPoint?.modelNorth || s?.modelNorth;
  if (mn && typeof mn.x === "number" && typeof mn.y === "number" && (mn.x || mn.y)) {
    const deg = Math.atan2(mn.x, mn.y) * (180 / Math.PI);
    return ((deg % 360) + 360) % 360;
  }
  return 0;
}

